import { createAction, props } from '@ngrx/store';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../../models/complex/time-recording-entry.model';
import { TimesheetState } from '../timesheet-collection.state';
import { TimesheetEntryPatch } from '../utils/timesheet-form-model';
import { NotificationIdState } from '../../utilities/notification-id.type';

/**
 * Timesheet store actions.
 *
 * Payloads are restricted to the serializable `JsonTimesheetModel` shape (or
 * primitives) so they pass the root store's `strictActionSerializability` check.
 * Write/transformation logic that operates on `*Model` instances stays in the
 * `TimesheetAndExpensesService` facade; only the resulting storage emissions flow
 * through these actions.
 *
 * `correlationId` on the save outcome actions lets the facade match a persisted
 * result back to the specific `saveTimesheetRequested` it dispatched, so its
 * returned observable settles for that write rather than for a concurrent one.
 */

// --- Lifecycle ---------------------------------------------------------------

/** Dispatched by the facade's `init()` to start the storage-sync effect. */
export const initTimesheet = createAction('[Timesheet] Init');

/** Dispatched once the facade has finished initialising (seeded + first sync). */
export const timesheetInitialized = createAction('[Timesheet] Initialized');

// --- Selection (view state) --------------------------------------------------

/**
 * Dispatched when the user picks a timesheet to view. Selection is view state: no
 * persist effect follows, and the selector falls back to the newest timesheet when
 * the id is unknown.
 */
export const timesheetSelected = createAction('[Timesheet] Selected', props<{ timesheetId: string }>());

// --- Storage rehydration (dispatched by the rehydrate effects at init + sync) ---

// storage-sync payloads carry `null` for the missing case: the strict action
// serializability runtime check rejects explicitly undefined payload values
/**
 * What storage holds, on its way into the slice.
 *
 * `now` and `retentionDays` travel with the timesheets because the reducer applies the retention
 * window and cannot read the clock or a config slice itself. Both are stamped by the effect that
 * bridges the hydrate result, the same way `buildTimesheetRows` is handed the current instant
 * rather than reading it.
 */
export const timesheetsChangedFromStorage = createAction(
    '[Timesheet] Timesheets changed in storage',
    props<{ timesheets: TimesheetState[]; now: string; retentionDays: number }>(),
);

// --- Current-timesheet writes (write path through the reducer) ---------------

/**
 * Dispatched when the timesheet form commits its validated draft — one action for the whole
 * form, so adding a field adds a property rather than an action.
 *
 * `entries` are patches keyed by `entryId`: the reducer merges them into the addressed
 * timesheet's entries instead of replacing them, so the fields the form does not edit (work
 * code, distance, notification, …) cannot be lost. Adding or deleting an entry is structural
 * and travels as its own action.
 */
export const timesheetEdited = createAction(
    '[Timesheet] Entries committed',
    props<{
        timesheetId: string;
        /** ISO */
        startOfWork: string;
        /** ISO */
        endOfWork: string;
        entries: TimesheetEntryPatch[];
        correlationId: string;
    }>(),
);

/**
 * Dispatched when the user closes a gap by adding a manual entry.
 *
 * Structural rather than part of the form commit: the form edits the entries that exist, and
 * `entryId` has to be minted outside a reducer. The new entry takes the gap's span, which is
 * exactly the time the user is accounting for.
 */
export const timesheetManualEntryAdded = createAction(
    '[Timesheet] Manual entry added',
    props<{
        timesheetId: string;
        entryId: string;
        /** ISO */
        start: string;
        /** ISO */
        end: string;
        notificationId: NotificationIdState;
        /**
         * Set when the entry continues a travel-back leg, which fixes its kind — the user then has
         * no task type to choose. Omitted for a plain gap-filling entry.
         */
        travelType?: TIMERECORDING_TYPE_OF_WORK_CODES;
        /** inherited from the leg being continued; defaults to false for a plain entry */
        isTravelBillable?: boolean;
        correlationId: string;
    }>(),
);

/** Dispatched when the user deletes a manual entry they had added. */
export const timesheetEntryDeleted = createAction(
    '[Timesheet] Entry deleted',
    props<{ timesheetId: string; entryId: string; correlationId: string }>(),
);

/**
 * Dispatched by the facade's `saveStartOfWork`. The reducer writes the new
 * start of work into the slice's current timesheet; the persist effect then
 * hands the updated slice to the save effect under the same `correlationId`.
 */
export const updateStartOfWork = createAction(
    '[Timesheet] Update start of work',
    props<{ startOfWork: string; correlationId: string }>(),
);

/**
 * Dispatched when a notification load finds the current timesheet's end of work on an earlier
 * day than today with no notification in progress (was
 * `TimesheetAndExpensesInterface.deleteSubmittedNotificationInfoFromTimesheet`). The reducer
 * drops the already-confirmed notifications from the slice's current timesheet; the persist
 * effect then hands the updated slice to the save effect under the same `correlationId`.
 */
export const removeSubmittedNotificationsFromCurrentTimesheet = createAction(
    '[Timesheet] Remove submitted notifications from current timesheet',
    props<{ correlationId: string }>(),
);

// --- Submit (backend post, slice swap, persistence, follow-ups) --------------

/**
 * Dispatched by the facade's `submit`. The submit effect reads the current
 * timesheet slice, builds the serialized OData out model (via
 * `buildTimesheetOutModel`) and the serialized empty replacement timesheet (its
 * shape depends on `UserMasterService.isPaceOnlineUser()`, which a reducer cannot
 * inject), so only the correlation id needs to cross into the store.
 */
export const submitTimesheetRequested = createAction(
    '[Timesheet] Submit requested',
    props<{ correlationId: string }>(),
);

/**
 * Dispatched by the submit effect once the backend accepted the timesheet.
 * The reducer marks the submitted timesheet and seeds the new empty current one under
 * `newTimesheetId` — generated by the effect, because a reducer cannot call `getUuidV4()`
 * and stay pure.
 */
export const timesheetSubmitted = createAction(
    '[Timesheet] Submitted to backend',
    props<{ correlationId: string; newTimesheetId: string }>(),
);

/** Dispatched once both swapped timesheets have been persisted to storage. */
export const timesheetSubmitPersisted = createAction(
    '[Timesheet] Submit persisted',
    props<{ correlationId: string }>(),
);

/** Dispatched once the offline queue flush following the submit has completed. */
export const timesheetSubmitSynced = createAction(
    '[Timesheet] Submit offline data synced',
    props<{ correlationId: string }>(),
);

/**
 * Single failure funnel for every submit stage (backend post, persistence,
 * offline sync). `error` carries the raw error string so the facade can
 * re-throw it verbatim — the timesheet component matches submit errors against
 * error-code enums.
 */
export const timesheetSubmitFailed = createAction(
    '[Timesheet] Submit failed',
    props<{ error: string; correlationId: string }>(),
);

// --- Persistence (write path through the save effect) ------------------------

/** Dispatched by the facade's save methods; the save effect persists the timesheet. */
export const saveTimesheetCollectionRequested = createAction(
    '[Timesheet] Save requested',
    props<{ correlationId: string }>(),
);

/**
 * Dispatched by the save effect once the timesheet slice has been persisted. Carries the
 * persisted shape — identical to the slice the write reducer holds — which the facade
 * rehydrates as its return value. The slice itself already holds the edit (applied
 * optimistically by the write reducer); this action only reports the persistence outcome to
 * the facade.
 */
export const timesheetCollectionSaved = createAction('[Timesheet] Saved', props<{ correlationId?: string }>());

export const timesheetCollectionStorageSyncError = createAction(
    '[Timesheet] Storage sync error',
    props<{ error: string; correlationId?: string }>(),
);

// --- Debug / support tooling (pace-spector) -----------------------------------

/**
 * Dispatched by pace-spector to discard one timesheet outright, for clearing local state a
 * technician got stuck in without waiting for a backend round-trip.
 *
 * Removing the current (unsubmitted) timesheet would violate the "exactly one open timesheet"
 * invariant, so the reducer reseeds a fresh empty one under a freshly minted id in its place;
 * a submitted timesheet is simply dropped.
 */
export const timesheetRemoved = createAction(
    '[Timesheet] Removed',
    props<{ timesheetId: string; correlationId: string }>(),
);

/**
 * Dispatched by pace-spector to discard every timesheet the slice holds, replacing them with one
 * fresh empty current timesheet — the same invariant-preserving reset `initialTimesheetCollectionState`
 * seeds the app with.
 */
export const allTimesheetsRemoved = createAction('[Timesheet] All removed', props<{ correlationId: string }>());
